/**
 * Doctor Profile management.
 */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { doctorAPI } from '../../services/api';
import Layout from '../../components/Layout';
import toast from 'react-hot-toast';

const EDITABLE_FIELDS = [
  'full_name',
  'phone_number',
  'specialization',
  'services_offered',
  'hospital_affiliation',
  'years_of_experience',
  'bio',
  'consultation_fee',
  'available_hours',
  'county',
];

const DoctorProfile = () => {
  const { userProfile, logout } = useAuth();
  const [profile, setProfile] = useState(null);
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadProfile = async () => {
    try {
      const res = await doctorAPI.getProfile();
      setProfile(res.data.profile || res.data);
    } catch {
      setProfile(userProfile || {});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProfile();
  }, [userProfile]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = EDITABLE_FIELDS.reduce((acc, key) => {
        if (profile?.[key] !== undefined) {
          acc[key] = profile[key];
        }
        return acc;
      }, {});

      await doctorAPI.updateProfile(payload);
      await loadProfile();
      toast.success('Profile updated successfully.');
      setEditing(false);
    } catch (error) {
      toast.error(error.message || 'Failed to update profile.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout title="Profile" role="doctor" showBack>
        <div className="p-4 space-y-4">
          {[1,2,3].map(i => <div key={i} className="skeleton h-16 rounded-2xl" />)}
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Profile" role="doctor" showBack>
      <div className="p-4 pb-24 space-y-4">
        {/* Avatar & Name */}
        <div className="card flex items-center gap-4">
          <div className="w-16 h-16 bg-accent-100 rounded-full flex items-center justify-center">
            <span className="text-accent-700 text-2xl font-bold">
              {(profile?.full_name || 'D').charAt(0)}
            </span>
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900">Dr. {profile?.full_name}</h2>
            <p className="text-sm text-accent-600">{profile?.specialization}</p>
            <div className="flex items-center gap-2 mt-1">
              <span className="px-2 py-0.5 bg-accent-100 text-accent-700 text-xs font-medium rounded-full">Doctor</span>
              {profile?.is_verified && (
                <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">✓ Verified</span>
              )}
            </div>
          </div>
        </div>

        {/* Fields */}
        <div className="card space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">Professional Information</h3>
            <button onClick={() => setEditing(!editing)} className="text-accent-500 text-sm font-medium">
              {editing ? 'Cancel' : 'Edit'}
            </button>
          </div>

          {[
            { label: 'Full Name', key: 'full_name' },
            { label: 'Email', key: 'email', disabled: true },
            { label: 'Phone Number', key: 'phone_number' },
            { label: 'License Number', key: 'license_number', disabled: true },
            { label: 'Specialization', key: 'specialization' },
            { label: 'Hospital', key: 'hospital_affiliation' },
            { label: 'County', key: 'county' },
            { label: 'Consultation Fee (KES)', key: 'consultation_fee', type: 'number' },
          ].map(field => (
            <div key={field.key}>
              <label className="label">{field.label}</label>
              {editing && !field.disabled ? (
                <input
                  type={field.type || 'text'}
                  value={profile?.[field.key] || ''}
                  onChange={e => setProfile(p => ({ ...p, [field.key]: e.target.value }))}
                  className="input-field"
                />
              ) : (
                <>
                  <p className="text-gray-900">{profile?.[field.key] || '—'}</p>
                  {field.key === 'license_number' && (
                    <p className="text-xs text-gray-500 mt-1">License number is locked after registration and cannot be changed.</p>
                  )}
                </>
              )}
            </div>
          ))}

          <div>
            <label className="label">Bio</label>
            {editing ? (
              <textarea
                value={profile?.bio || ''}
                onChange={e => setProfile(p => ({ ...p, bio: e.target.value }))}
                className="input-field"
                rows="3"
              />
            ) : (
              <p className="text-gray-900 text-sm">{profile?.bio || '—'}</p>
            )}
          </div>

          {editing && (
            <button onClick={handleSave} disabled={saving} className="w-full py-3 rounded-xl font-semibold text-white bg-accent-500 hover:bg-accent-600 transition-colors flex items-center justify-center gap-2">
              {saving && <div className="spinner w-5 h-5 border-2"></div>}
              Save Changes
            </button>
          )}
        </div>

        <button onClick={logout} className="btn-secondary w-full text-red-600">
          Sign Out
        </button>
      </div>
    </Layout>
  );
};

export default DoctorProfile;
